import { useEffect, useState, useRef } from 'react';
import { saveStoreSettings, getStoreSettings } from '../utils/saveStoreSettings';
import { setStoreIdentity, persistStoreSession } from '../utils/storeSession';
import { useTranslation } from 'react-i18next';
import { buildStoreSettingsPayload } from '../utils/storeDisplaySettings';

export const useStoreSettings = (homeData, isLoading, slug) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const [settingsLoaded, setSettingsLoaded] = useState(false);
    const { i18n } = useTranslation();

    const initializedSlugs = useRef(new Set());

    useEffect(() => {
        setIsInitialized(false);
        setSettingsLoaded(false);
    }, [slug]);

    // Keep slug-scoped settings in sync whenever home/store API data changes.
    useEffect(() => {
        if (!slug || isLoading || !homeData) return;
        const payload = buildStoreSettingsPayload(homeData);
        if (!payload) return;
        saveStoreSettings(payload, slug).catch((err) => {
            console.error('[useStoreSettings] settings sync failed:', err);
        });
    }, [
        slug,
        isLoading,
        homeData,
        homeData?.identity_color,
        homeData?.lang,
        homeData?.products_show_method,
        homeData?.default_page,
        homeData?.views_active,
        homeData?.ratings_active,
    ]);

    useEffect(() => {
        if (!slug) return;

        if (!isLoading && homeData && !isInitialized) {
            const defaultLang = homeData?.lang || 'ar';

            setStoreIdentity('country-id', homeData?.country?.id || '', slug);
            setStoreIdentity('type', homeData?.type || '', slug);
            setStoreIdentity('features_subscriptions', homeData?.features_subscriptions || [], slug);
            setStoreIdentity('liked', homeData?.liked || 'false', slug);
            setStoreIdentity('package_type', homeData?.currentSubscription?.package_type || '', slug);
            setStoreIdentity('has_private_package', homeData?.has_private_package || 'false', slug);
            setStoreIdentity('store-id', homeData?.id || '', slug);
            setStoreIdentity('store-whats', homeData?.cart_whatssapp_number || {}, slug);
            setStoreIdentity('cart_active', homeData?.cart_active || 'false', slug);
            setStoreIdentity('store_network', homeData?.store_network ?? '', slug);
            persistStoreSession(slug);

            localStorage.setItem('initialStoreSlug', slug);
            localStorage.setItem('initialStoreLogo', homeData?.logo || '');
            localStorage.setItem('store_network', homeData?.store_network || '');

            // Prefer the visitor's explicit choice over the store default.
            // Never clear lang_user_changed on init — that caused the first language-switch
            // click to look like a reload while the page snapped back to the store default
            // after StoreProvider remounted on the home refetch.
            let langToApply = defaultLang;
            try {
                const userChanged =
                    localStorage.getItem(`lang_user_changed-${slug}`) === 'true';
                const savedLang = localStorage.getItem(`store_i18nextLng-${slug}`);
                const isValidSaved =
                    savedLang === 'ar' || savedLang === 'en';

                if (userChanged && isValidSaved) {
                    langToApply = savedLang;
                } else {
                    localStorage.setItem(`store_i18nextLng-${slug}`, defaultLang);
                    langToApply = defaultLang;
                }
                localStorage.setItem('i18nextLng', langToApply);
            } catch (e) {
                // silent
            }

            initializedSlugs.current.add(slug);
            i18n.changeLanguage(langToApply)
                .then(() => {
                    document.documentElement.dir = langToApply === 'ar' ? 'rtl' : 'ltr';
                    document.documentElement.lang = langToApply;
                    setSettingsLoaded(true);
                    setIsInitialized(true);
                })
                .catch(() => {
                    setIsInitialized(true);
                    setSettingsLoaded(true);
                });
        }
    }, [isLoading, homeData, isInitialized, slug, i18n]);

    return {
        isInitialized,
        settingsLoaded,
        settings: getStoreSettings(slug),
    };
};
